'use client';

import {formatCny, type PublicCatalogModelData, type PublicCatalogPageData, type PublicCatalogPricePlanData} from '@zzy/contracts';
import {Button} from '../../components/ui/button';
import {catalogCategories, type CatalogReadState} from './content/api-platform';
import styles from './api-platform.module.css';

type Copy = (text: string) => string;
export type CatalogDetailView = 'prices' | 'specs' | 'docs';

const unknownPriceCopy: Record<Extract<PublicCatalogPricePlanData, {status: 'unknown'}>['reason'], string> = {
  missing_price: '价格尚未提供', unsupported_expression: '计费规则待核对', incomplete_unit: '计费单位待核对',
  incomplete_group_price: '分组报价待核对', unrepresentable_amount: '金额精度待核对',
  projection_limit: '完整报价待核对', not_verified: '报价尚未核验',
};
const unitCopy = {request: '次请求', second: '秒', token: 'Token', image: '张图片', character: '字符', credit: '计费用量单位', count: '次'} as const;
const modeCopy = {request: '按次计费', token: 'Token 计费', tiered: '分档计费', usage: '按用量计费'} as const;

export function CatalogIcon({name}: {name: 'copy' | 'search' | 'refresh'}) {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {name === 'copy' ? <><rect x="8" y="8" width="12" height="13" rx="2"/><path d="M15 4H6a2 2 0 0 0-2 2v9"/></> : name === 'search' ? <><circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 4 4"/></> : <><path d="M20 8a8 8 0 1 0 .3 7M20 3v5h-5"/></>}
  </svg>;
}

export function CatalogPricePlans({plans, t}: {plans: PublicCatalogPricePlanData[]; t: Copy}) {
  return <div className={styles.pricePlans}>{plans.map(plan => <section className={styles.pricePlan} key={plan.id} aria-label={plan.label}>
    <h5>{plan.label} · {t(plan.status === 'known' ? modeCopy[plan.mode] : '价格待核对')}</h5>
    {plan.status === 'unknown' ? <p className={styles.detailNote}>{t(unknownPriceCopy[plan.reason])}</p> : plan.tiers.map((tier, index) => <section className={styles.priceTier} key={tier.id}>
      <h6>{index + 1}. {tier.label}</h6>
      <p>{tier.condition.kind === 'always' ? t('适用于本档全部用量') : tier.condition.text}</p>
      <dl className={styles.priceLines}>{tier.lines.map(line => <div key={line.id}>
        <dt>{line.label}{line.usageField && <code>{line.usageField}</code>}</dt>
        <dd><strong>{formatCny(line.amount)}</strong><span> / {line.denominator.quantity} {t(unitCopy[line.denominator.unit])} · {line.denominator.label}</span></dd>
      </div>)}</dl>
    </section>)}
  </section>)}</div>;
}

function CatalogPriceSummary({plans, t}: {plans: PublicCatalogPricePlanData[]; t: Copy}) {
  const plan = plans.length === 1 ? plans[0] : undefined;
  const unknownCount = plans.filter(value => value.status === 'unknown').length;
  const tier = plan?.status === 'known' && plan.mode !== 'tiered' && plan.tiers.length === 1 ? plan.tiers[0] : undefined;
  const simpleTier = tier?.condition.kind === 'always' && (tier.lines.length === 1 ||
    tier.lines.length === 2 && tier.lines.every(line => line.denominator.unit === 'token') &&
    tier.lines.some(line => line.component === 'input') && tier.lines.some(line => line.component === 'output')) ? tier : undefined;

  if (simpleTier) return <div className={styles.priceSummary}>{simpleTier.lines.map(line => <div className={styles.summaryLine} key={line.id}>
    <span title={line.label}>{line.label}</span>
    <strong title={`${formatCny(line.amount)} / ${line.denominator.quantity} ${t(unitCopy[line.denominator.unit])} · ${line.denominator.label}`}>{formatCny(line.amount)}<small> / {line.denominator.quantity} {t(unitCopy[line.denominator.unit])}</small></strong>
  </div>)}</div>;

  return <div className={styles.complexPrice}>
    <strong>{t(unknownCount === plans.length ? '价格待核对' : unknownCount > 0 ? '部分价格待核对' : plans.length > 1 ? '多方案计费' : plan?.status === 'known' && plan.mode === 'tiered' ? '分档计费' : '多项计费')}</strong>
    <span title={plan?.label}>{plan?.status === 'unknown' ? t(unknownPriceCopy[plan.reason]) : plan?.label ?? `${plans.length} ${t('个计费方案')}`}</span>
  </div>;
}

export function CatalogReadStatus({state, t, onRetry}: {state: CatalogReadState; t: Copy; onRetry?: () => void}) {
  if (state.status === 'success') return state.page.status === 'empty' ? <div className={styles.empty} role="status">
    <strong>{t('没有匹配的模型')}</strong><p>{t('请调整搜索、分类、厂商或分组条件。')}</p>
  </div> : null;

  if (state.status === 'loading') return <div role="status" aria-busy="true">
    <span className="sr-only">{t('正在读取模型与价格')}</span>
    <div className={styles.modelGrid} aria-hidden="true">{Array.from({length: 6}, (_, index) => <div className={styles.skeletonCard} key={index}>
      <span/><span/><span/><span/>
    </div>)}</div>
  </div>;

  const unavailable = state.status === 'unavailable';
  const reason = state.status === 'error' ? state.reason : undefined;
  const detail = unavailable ? '模型与价格目录暂时不可查询，请稍后重试。' : reason === 'snapshot_changed' ? '目录已更新，请重新读取第一页。' : reason === 'timeout' ? '目录读取超时，请稍后重试。' : '目录读取失败，请稍后重试。';
  return <div className={styles.readError} role={unavailable ? 'status' : 'alert'}>
    <div><strong>{t(unavailable ? '模型目录暂不可用' : '模型目录读取失败')}</strong><p>{t(detail)}</p></div>
    {onRetry && <Button variant="outline" onClick={onRetry}>{t('重新读取')}</Button>}
  </div>;
}

export function CatalogModelResults({page, groupId, t, onDetail, onCopy}: {
  page: PublicCatalogPageData; groupId?: string; t: Copy;
  onDetail?: (model: PublicCatalogModelData, view: CatalogDetailView) => void; onCopy?: (text: string) => void;
}) {
  return <div className={styles.modelGrid}>{page.items.map(model => {
    const group = groupId ? model.pricesByGroup.find(value => value.groupId === groupId) : model.pricesByGroup[0]!;
    const groupName = page.groups.find(value => value.id === (group?.groupId ?? groupId))?.name ?? groupId!;
    const vendorName = page.vendors.find(value => value.id === model.vendorId)?.name;
    const categoryName = catalogCategories.find(category => category.id === model.category)!.label;
    return <article className={styles.modelCard} key={model.id}>
      <header className={styles.cardHeader}>
        <div><h3 title={model.name}>{model.name}</h3><p>{vendorName || t('厂商未提供')}</p></div>
        <span className={styles.categoryBadge}>{t(categoryName)}</span>
      </header>
      <p className={styles.modelDescription}>{model.description || t('模型说明待补充')}</p>
      <div className={styles.modelId}><code title={model.id}>{model.id}</code><Button variant="ghost" size="icon-sm" aria-label={t('复制模型 ID') + ' ' + model.id} onClick={() => onCopy?.(model.id)}><CatalogIcon name="copy"/></Button></div>
      <div className={styles.tags} aria-label={t('模型能力')}>{model.tags.slice(0, 3).map((tag, index) => <span key={index} title={tag}>{tag}</span>)}{model.tags.length > 3 && <span title={t('在模型详情中查看全部能力')}>+{model.tags.length - 3}</span>}</div>
      <div className={styles.cardPrice}><span className={styles.priceGroup} title={groupName}>{t('报价分组')} · {groupName}</span>{group ? <CatalogPriceSummary plans={group.plans} t={t}/> : <div className={styles.complexPrice}><strong>{t('暂无分组报价')}</strong><span>{t('请在详情中查看其他分组。')}</span></div>}</div>
      <Button className={styles.detailButton} variant="outline" onClick={() => onDetail?.(model, 'prices')}>{t('查看详情')}<span aria-hidden="true">→</span></Button>
    </article>;
  })}</div>;
}

export function CatalogModelDetails({model, page, t, view, onCopy}: {
  model: PublicCatalogModelData; page: PublicCatalogPageData; t: Copy; view: CatalogDetailView; onCopy?: (text: string) => void;
}) {
  const groupName = (id: string) => page.groups.find(group => group.id === id)?.name ?? id;
  return <>
    <p className={styles.detailDescription}>{model.description || t('模型说明待补充')}</p>
    <div className={styles.detailModelId}><code>{model.id}</code><Button variant="ghost" size="icon-sm" aria-label={t('复制模型 ID')} onClick={() => onCopy?.(model.id)}><CatalogIcon name="copy"/></Button></div>
    {view === 'prices' ? <div className={styles.detailGroups}>{model.pricesByGroup.map(group => <section className={styles.detailGroup} key={group.groupId}>
      <h3>{groupName(group.groupId)}</h3><CatalogPricePlans plans={group.plans} t={t}/>
    </section>)}</div> : view === 'specs' ? <>
      <dl className={styles.metadata}>
        <div><dt>{t('分类')}</dt><dd>{t(catalogCategories.find(category => category.id === model.category)!.label)}</dd></div>
        <div><dt>{t('厂商')}</dt><dd>{page.vendors.find(vendor => vendor.id === model.vendorId)?.name ?? t('未提供')}</dd></div>
        <div><dt>{t('可见分组')}</dt><dd>{model.groups.map(groupName).join(' / ')}</dd></div>
        {model.specifications.map(spec => <div key={spec.key}><dt>{spec.label}</dt><dd>{spec.value}{spec.unit && ' ' + spec.unit}</dd></div>)}
      </dl>
      {model.specificationCoverage === 'unknown' && <p className={styles.detailNote}>{t('模型规格尚未完整核验；已列出的规格按原文展示。')}</p>}
      {model.tags.length > 0 && <div className={styles.detailTags}>{model.tags.map((tag, index) => <span key={index}>{tag}</span>)}</div>}
    </> : <div className={styles.endpoints}>
      <h3>{t('公开接口信息')}</h3>
      {model.endpoints.length ? model.endpoints.map((endpoint, index) => <div key={index}>
        <span>{endpoint.method} · {endpoint.type}</span><code>{endpoint.path}</code>
        <Button variant="ghost" size="icon-sm" aria-label={t('复制接口路径') + ' ' + endpoint.path} onClick={() => onCopy?.(endpoint.path)}><CatalogIcon name="copy"/></Button>
      </div>) : <p>{t('公开接口信息尚未提供')}</p>}
      <p className={styles.detailNote}>{t('接口路径仅供查阅，完整参数请以对应服务的模型文档为准。')}</p>
    </div>}
    <p className={styles.detailNote}>{t('实际调用状态')}：{t('尚未核验')}。{t('公开目录与报价不代表生成服务、账户授权或实际扣费已验证。')}</p>
  </>;
}
